import { useState, type FormEvent } from "react";
import type { Profile, TravelPace } from "../types";
import { CityCombobox } from "./CityCombobox";
import { Icon } from "./Icon";

interface OnboardingProps {
  onComplete: (profile: Profile) => void;
  onSignIn: () => void;
}

const paceOptions: Array<{ value: TravelPace; title: string; detail: string }> = [
  { value: "relaxed", title: "Room to wander", detail: "Fewer anchors and more time for detours." },
  { value: "balanced", title: "A steady rhythm", detail: "Plans and breathing room in equal measure." },
  { value: "packed", title: "Make every hour count", detail: "More stops, tighter routing, earlier starts." },
];

const currencies = ["USD", "EUR", "GBP", "JPY", "VND", "CAD", "AUD", "SGD", "KRW", "THB"];

export function Onboarding({ onComplete, onSignIn }: OnboardingProps) {
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState<Profile>({
    name: "",
    homeCity: "",
    pace: "balanced",
    currency: "USD",
  });
  const [error, setError] = useState("");

  function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (step === 1) {
      setStep(2);
      return;
    }
    onComplete({ ...profile, name: profile.name.trim(), homeCity: profile.homeCity.trim() });
  }

  return (
    <main id="main" className="onboarding-shell">
      <header className="onboarding-header">
        <div className="onboarding-brand">
          <span className="brand-route"><Icon name="route" size={20} /></span>
          <strong>roam</strong>
        </div>
        <div className="onboarding-account"><span className="local-label">Local-first trip planning</span><button className="button button-quiet" type="button" onClick={onSignIn}><Icon name="login" size={16} /> Sign in</button></div>
      </header>

      <div className="onboarding-layout">
        <section className="onboarding-intro" aria-labelledby="onboarding-title">
          <h1 id="onboarding-title">Plan the route.<br />Keep control.</h1>
          <p>Build each day on a timeline and map. Your browser agent can handle the planning work you choose to delegate.</p>
          <dl className="onboarding-facts">
            <div><dt>Storage</dt><dd>Local-first</dd></div>
            <div><dt>Place data</dt><dd>OpenStreetMap</dd></div>
            <div><dt>Control</dt><dd>You review broad changes</dd></div>
          </dl>
        </section>

        <section className="onboarding-workspace" aria-label={`Profile setup, step ${step} of 2`}>
          <div className="step-header">
            <span>Step {step} of 2</span>
            <div className="step-dots" aria-hidden="true"><i className="is-complete" /><i className={step === 2 ? "is-complete" : ""} /></div>
          </div>

          <form onSubmit={submit} className="onboarding-form">
            {step === 1 ? (
              <>
                <div className="form-intro">
                  <h2>Start with home.</h2>
                  <p>Roam uses this to personalize new trips. You can change it later.</p>
                </div>
                <label className="field">
                  <span>Your name <small>optional</small></span>
                  <input autoFocus autoComplete="name" value={profile.name} onChange={(event) => setProfile({ ...profile, name: event.target.value })} placeholder="How should Roam greet you?" />
                </label>
                <CityCombobox label="Home city (optional)" value={profile.homeCity} onChange={(homeCity) => setProfile({ ...profile, homeCity })} />
              </>
            ) : (
              <>
                <div className="form-intro">
                  <h2>Set your defaults.</h2>
                  <p>These shape new trips without limiting what you can change later.</p>
                </div>
                <fieldset className="choice-list">
                  <legend>Travel pace</legend>
                  {paceOptions.map((option) => (
                    <label key={option.value} className={`choice-row ${profile.pace === option.value ? "is-selected" : ""}`}>
                      <input type="radio" name="pace" value={option.value} checked={profile.pace === option.value} onChange={() => setProfile({ ...profile, pace: option.value })} />
                      <span><strong>{option.title}</strong><small>{option.detail}</small></span>
                      <Icon name="check" size={17} />
                    </label>
                  ))}
                </fieldset>
                <label className="field">
                  <span>Planning currency <small>optional</small></span>
                  <select value={profile.currency} onChange={(event) => setProfile({ ...profile, currency: event.target.value })}>
                    {currencies.map((currency) => <option key={currency} value={currency}>{currency}</option>)}
                  </select>
                </label>
                <div className="privacy-note"><Icon name="check" size={18} /><p><strong>Local by default.</strong> Roam stores trips in this browser. Sign in later only if you want account sync.</p></div>
              </>
            )}

            <p className="form-error" role="alert">{error}</p>
            <div className="onboarding-actions">
              {step === 2 ? <button className="button button-secondary" type="button" onClick={() => { setError(""); setStep(1); }}><Icon name="arrow-left" /> Back</button> : <button className="button button-quiet" type="button" onClick={() => onComplete({ ...profile, name: profile.name.trim(), homeCity: profile.homeCity.trim() })}>Skip for now</button>}
              <button className="button button-primary" type="submit">{step === 2 ? "Enter Roam" : "Continue"}<Icon name="arrow-right" /></button>
            </div>
          </form>
        </section>
      </div>
    </main>
  );
}
